/* eslint-disable @next/next/no-img-element */
import { LoadingButton } from "@mui/lab";
import {
  Avatar,
  Box,
  Button,
  CircularProgress,
  DialogActions,
  DialogContent,
  FormControl,
  IconButton,
  InputLabel,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useFormik } from "formik";
import { useSnackbar } from "notistack";
import React, { useRef, useState } from "react";
import { useAppDispatch } from "../../app/hooks";
import { StyledTextField } from "../../pages/login";
import { editProfileSchema } from "../../schema";
import {
  useUpdateCoreFieldsMutation,
  useUpdateProfilePicMutation,
} from "../../services/profileAPI";
import { setCredentials } from "../../slices/authSlice";
import { CustomDialogTitle } from "../../ui";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { grey } from "@mui/material/colors";
import ReactCrop, {
  centerCrop,
  makeAspectCrop,
  Crop,
  PixelCrop,
  PercentCrop,
} from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";
import CloseIcon from "@mui/icons-material/Close";
import { mediaUploadHandler } from "../../utils/mediaUpload";
import { uploadTypes } from "../../constants";
import { useDebounceEffect } from "../../utils/useDebounceEffect";
import { canvasPreview } from "../../utils/canvasPreview";
import { imgPreview } from "../../utils/imagePreview";

function centerAspectCrop(
  mediaWidth: number,
  mediaHeight: number,
  aspect: number
) {
  return centerCrop(
    makeAspectCrop(
      {
        unit: "%",
        width: 80,
      },
      aspect,
      mediaWidth,
      mediaHeight
    ),
    mediaWidth,
    mediaHeight
  );
}

function EditProfileDialog({
  name,
  headline,
  workplace,
  city,
  image,
  onClose,
}: any) {
  const [updateCoreFields] = useUpdateCoreFieldsMutation();
  const dispatch = useAppDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const [crop, setCrop] = useState<boolean | PercentCrop | Crop | undefined>(
    false
  );
  const [imgSrc, setImgSrc] = useState("");
  const inputFile = useRef<any | null>(null);
  const imgRef = useRef<HTMLImageElement>(null);
  const [completedCrop, setCompletedCrop] = useState<PixelCrop | null>(null);
  const [updateProfilePic, { isLoading: isProfilePicUploading }] =
    useUpdateProfilePicMutation();
  const previewCanvasRef = useRef<HTMLCanvasElement>(null);

  const formik = useFormik({
    initialValues: {
      name,
      headline,
      workplace,
      city,
    },
    validationSchema: editProfileSchema,
    onSubmit: async (values) => {
      try {
        await updateCoreFields({
          name: values.name,
          headline: values.headline,
          city: values.city,
          currentCompany: values.workplace,
        }).unwrap();
        const credentials = {
          name: values.name || "",
        };
        dispatch(setCredentials(credentials));
        enqueueSnackbar("updated your details", {
          variant: "success",
        });
        onClose();
      } catch (error) {
        console.error(error);
        enqueueSnackbar("failed to update your details", {
          variant: "error",
        });
      }
    },
  });

  const onSelectFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      setCrop(undefined); // Makes crop preview update between images.
      const reader = new FileReader();
      reader.addEventListener("load", () =>
        setImgSrc(reader?.result?.toString() || "")
      );
      reader.readAsDataURL(e.target.files[0]);
    }
  };

  const onButtonClick = () => {
    inputFile?.current?.click();
  };

  const onImageLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const { width, height } = e.currentTarget;
    setCrop(centerAspectCrop(width, height, 1));
  };

  useDebounceEffect(
    async () => {
      if (
        completedCrop?.width &&
        completedCrop?.height &&
        imgRef.current &&
        previewCanvasRef.current
      ) {
        // We use canvasPreview as it's much faster than imgPreview.
        canvasPreview(
          imgRef.current,
          previewCanvasRef.current,
          completedCrop,
          1,
          0
        );
      }
    },
    100,
    [completedCrop]
  );

  const onUploadClickHandler = async () => {
    try {
      const result = await imgPreview(
        imgRef?.current!,
        previewCanvasRef?.current!,
        completedCrop!,
        1,
        0
      );
      let newImage = new File([result.blob], "profilepic", {
        type: result.blob.type,
      });

      const imageKey = await mediaUploadHandler(
        newImage,
        uploadTypes.PROFILE_PICS
      );
      if (imageKey) {
        const response = await updateProfilePic({
          profilePicKey: imageKey,
        }).unwrap();
        if (response?.image) {
          const userImage = response.image || "";
          const credentials = {
            userImage,
          };
          dispatch(setCredentials(credentials));
          enqueueSnackbar("profile picture updated successfully", {
            variant: "success",
          });
        }
      }
    } catch (error) {
      console.log(error);
      enqueueSnackbar("an error occured while uploading image", {
        variant: "error",
      });
    }
  };

  return (
    <>
      <CustomDialogTitle onClose={onClose}>
        <Typography>edit profile</Typography>
      </CustomDialogTitle>
      <form onSubmit={formik.handleSubmit}>
        <DialogContent dividers>
          <Stack alignItems="center">
            <Box sx={{ position: "relative" }}>
              <Avatar
                src={image}
                alt="profile"
                sx={{
                  width: { xs: 80 },
                  height: { xs: 80 },
                }}
              />
              <Box
                sx={{
                  position: "absolute",
                  bottom: 0,
                  right: 0,
                  zIndex: 100,
                }}
              >
                <Box>
                  <IconButton
                    onClick={onButtonClick}
                    size="small"
                    sx={{
                      borderRadius: "100%",
                      boxShadow: "0px 0px 10px rgba(0, 0, 0, 0.2)",
                      backgroundColor: "#fff",
                      "&:hover": { backgroundColor: "#f5f5f5" },
                    }}
                  >
                    <EditOutlinedIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                  <input
                    type="file"
                    id="file"
                    style={{ display: "none" }}
                    name="image"
                    ref={inputFile}
                    accept="image/gif,image/jpeg,image/jpg,image/png"
                    multiple={false}
                    data-original-title="upload photos"
                    onChange={onSelectFile}
                  />
                </Box>
              </Box>
            </Box>

            {Boolean(imgSrc) && (
              <Box sx={{ position: "relative", mt: 3 }}>
                <Box
                  sx={{
                    position: "absolute",
                    top: -12,
                    right: -6,
                    zIndex: 5,
                  }}
                >
                  <IconButton
                    onClick={() => setImgSrc("")}
                    size="small"
                    sx={{
                      borderRadius: "100%",
                      boxShadow: "0px 0px 10px rgba(0, 0, 0, 0.2)",
                      backgroundColor: "#fff",
                      "&:hover": { backgroundColor: "#f5f5f5" },
                    }}
                  >
                    <CloseIcon sx={{ fontSize: "16px" }} />
                  </IconButton>
                </Box>
                <ReactCrop
                  crop={crop as Crop}
                  onChange={(_, percentCrop) => setCrop(percentCrop)}
                  onComplete={(c) => setCompletedCrop(c)}
                  aspect={1}
                  circularCrop={true}
                  minHeight={256}
                  minWidth={256}
                  maxHeight={256}
                  maxWidth={256}
                >
                  <img
                    ref={imgRef}
                    alt="Crop me"
                    src={imgSrc}
                    onLoad={onImageLoad}
                  />
                </ReactCrop>
                <Stack direction="row" justifyContent="center" mt={1.5}>
                  <Button
                    variant="contained"
                    color="secondary"
                    onClick={onUploadClickHandler}
                    disabled={isProfilePicUploading}
                    sx={{ fontSize: 14 }}
                  >
                    {isProfilePicUploading ? (
                      <CircularProgress color="primary" size={22} />
                    ) : (
                      "upload"
                    )}
                  </Button>
                </Stack>
              </Box>
            )}
          </Stack>

          {/* accessing image preview here */}
          <Stack>
            {Boolean(completedCrop) && (
              <canvas
                ref={previewCanvasRef}
                style={{
                  border: "1px solid black",
                  objectFit: "contain",
                  width: completedCrop?.width,
                  height: completedCrop?.height,
                  display: "none",
                }}
              />
            )}
          </Stack>

          <FormControl fullWidth variant="standard" margin="normal">
            <InputLabel shrink htmlFor="name" disableAnimation>
              name
            </InputLabel>
            <StyledTextField
              id="name"
              name="name"
              fullWidth
              placeholder="enter your name"
              value={formik.values.name}
              onChange={formik.handleChange}
              error={formik.touched.name && Boolean(formik.errors.name)}
              helperText={
                Boolean(formik.touched.name) && <>{formik.errors.name}</>
              }
            />
          </FormControl>

          <FormControl fullWidth variant="standard" margin="normal">
            <InputLabel shrink htmlFor="headline" disableAnimation>
              headline
            </InputLabel>
            <StyledTextField
              id="headline"
              name="headline"
              fullWidth
              multiline
              rows={4}
              placeholder="enter a headline"
              value={formik.values.headline}
              onChange={formik.handleChange}
              error={formik.touched.headline && Boolean(formik.errors.headline)}
              helperText={
                Boolean(formik.touched.headline) && (
                  <>{formik.errors.headline}</>
                )
              }
            />
          </FormControl>

          <FormControl fullWidth variant="standard" margin="normal">
            <InputLabel shrink htmlFor="workplace" disableAnimation>
              workplace
            </InputLabel>
            <StyledTextField
              id="workplace"
              name="workplace"
              fullWidth
              placeholder="enter workplace details"
              value={formik.values.workplace}
              onChange={formik.handleChange}
              error={
                formik.touched.workplace && Boolean(formik.errors.workplace)
              }
              helperText={
                Boolean(formik.touched.workplace) && (
                  <>{formik.errors.workplace}</>
                )
              }
            />
          </FormControl>

          <FormControl fullWidth variant="standard" margin="normal">
            <InputLabel shrink htmlFor="city" disableAnimation>
              city
            </InputLabel>
            <StyledTextField
              id="city"
              name="city"
              fullWidth
              placeholder="enter your city"
              value={formik.values.city}
              onChange={formik.handleChange}
              error={formik.touched.city && Boolean(formik.errors.city)}
              helperText={
                Boolean(formik.touched.city) && <>{formik.errors.city}</>
              }
            />
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} color="secondary">
            cancel
          </Button>
          <LoadingButton
            variant="text"
            type="submit"
            loading={formik.isSubmitting}
          >
            update
          </LoadingButton>
        </DialogActions>
      </form>
    </>
  );
}

export default EditProfileDialog;
